import { farShared } from "./farTokens.jsx";

/** Layout + table styles for the FAR management page (Rules / Settings / Audit Log). */
const farPageStyle = (theme) => {
    const s = farShared(theme);
    const t = s.t;
    return {
        page: {
            color: t.text,
            textAlign: "left",
            padding: "8px 24px 40px",
            boxSizing: "border-box",
            [theme.breakpoints.down("sm")]: { padding: "8px 12px 32px" }
        },
        header: {
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "20px",
            flexWrap: "wrap",
            marginBottom: "18px"
        },
        headerText: { flex: "1 1 360px", minWidth: 0 },
        title: { margin: 0, fontSize: "30px", lineHeight: "38px", fontWeight: 700, color: t.textStrong, [theme.breakpoints.down("sm")]: { fontSize: "24px", lineHeight: "30px" } },
        subtitle: { margin: "6px 0 0", fontSize: "15px", lineHeight: "22px", color: t.textMuted, maxWidth: "620px" },
        headerBanner: { flex: "0 1 420px" },
        tabs: {
            display: "flex",
            gap: "4px",
            borderBottom: "1px solid "+t.border,
            marginBottom: "18px",
            overflowX: "auto"
        },
        tab: {
            ...s.focusRing,
            padding: "12px 22px",
            border: "none",
            borderBottom: "3px solid transparent",
            background: "transparent",
            color: t.textMuted,
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
            borderRadius: "8px 8px 0 0",
            "&:hover": { color: t.text, backgroundColor: t.surfaceHover }
        },
        tabActive: {
            color: t.dark ? t.textStrong : t.accent,
            borderBottomColor: t.accent,
            backgroundColor: t.dark ? "rgba(59,130,246,0.14)" : t.info.bg
        },
        layout: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 300px",
            gap: "20px",
            alignItems: "start",
            [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" }
        },
        main: { minWidth: 0, display: "flex", flexDirection: "column", gap: "16px" },
        toolbar: { display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" },
        searchWrap: { position: "relative", flex: "1 1 280px", minWidth: 0, "& svg": { position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: t.textMuted, width: "20px", height: "20px", pointerEvents: "none" } },
        search: { ...s.input, paddingLeft: "40px", minHeight: "44px" },
        select: { ...s.input, width: "auto", minWidth: "160px", minHeight: "44px", cursor: "pointer", appearance: "auto" },
        addButton: { ...s.accentButton, minHeight: "44px", "& svg": { width: "20px", height: "20px" } },
        bulkBar: {
            display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
            padding: "10px 14px", borderRadius: "10px", backgroundColor: t.info.bg, border: "1px solid "+t.info.border,
            fontSize: "14px", fontWeight: 600
        },
        bulkButton: { ...s.secondaryButton, minHeight: "34px", padding: "0 12px", fontSize: "13px" },
        bulkDanger: { ...s.secondaryButton, minHeight: "34px", padding: "0 12px", fontSize: "13px", borderColor: t.danger.solid, color: t.danger.fg, "&:hover": { backgroundColor: t.danger.bg } },
        tableCard: { ...s.card, overflow: "hidden" },
        tableScroll: { overflowX: "auto" },
        table: {
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
            minWidth: "960px",
            "& th": {
                textAlign: "left", padding: "14px 12px", fontSize: "13px", fontWeight: 700, color: t.textStrong,
                borderBottom: "1px solid "+t.border, whiteSpace: "nowrap", backgroundColor: t.dark ? "rgba(255,255,255,0.02)" : t.surfaceAlt
            },
            "& td": { padding: "14px 12px", borderBottom: "1px solid "+t.border, verticalAlign: "middle", color: t.text },
            "& tbody tr:last-child td": { borderBottom: "none" },
            "& tbody tr:hover": { backgroundColor: t.surfaceHover },
            "& tbody tr[aria-selected='true']": { backgroundColor: t.dark ? "rgba(59,130,246,0.10)" : "#F1F6FF" }
        },
        checkCell: { width: "44px", "& input": { width: "18px", height: "18px", accentColor: t.accent, cursor: "pointer" } },
        sortButton: { ...s.focusRing, display: "inline-flex", alignItems: "center", gap: "4px", border: "none", background: "transparent", color: "inherit", font: "inherit", fontWeight: 700, cursor: "pointer", padding: 0 },
        ruleName: { margin: 0, fontWeight: 700, fontSize: "15px", color: t.textStrong },
        ruleDesc: { margin: "2px 0 0", fontSize: "13px", color: t.textMuted },
        conditions: { display: "flex", flexDirection: "column", gap: "4px", margin: 0, padding: 0, listStyle: "none" },
        condition: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", wordBreak: "break-all", "& svg": { width: "18px", height: "18px", flexShrink: 0 } },
        folderIcon: { color: t.dark ? "#FAAF42" : "#D97706" },
        fileIcon: { color: t.textMuted },
        op: { fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", color: t.textMuted, paddingLeft: "26px" },
        outlookOnly: {
            display: "inline-block", marginLeft: "4px", padding: "0 6px", borderRadius: "4px", fontSize: "11px", fontWeight: 600,
            color: t.neutral.fg, backgroundColor: t.neutral.bg, border: "1px solid "+t.neutral.border, whiteSpace: "nowrap"
        },
        webChip: {
            display: "inline-block", marginLeft: "4px", padding: "0 6px", borderRadius: "4px", fontSize: "11px", fontWeight: 600,
            color: t.info.fg, backgroundColor: t.info.bg, border: "1px solid "+t.info.border, whiteSpace: "nowrap"
        },
        emails: { display: "flex", flexDirection: "column", gap: "2px", fontSize: "13px", wordBreak: "break-all" },
        badges: { display: "flex", gap: "6px", flexWrap: "nowrap" },
        badge: s.badge,
        modified: { fontSize: "13px", whiteSpace: "nowrap", "& span": { display: "block", color: t.textMuted, fontSize: "12px" } },
        actions: { display: "flex", gap: "2px", whiteSpace: "nowrap" },
        iconButton: s.ghostIconButton,
        footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", padding: "4px 2px", fontSize: "14px", color: t.textMuted },
        pager: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
        pageButton: { ...s.ghostIconButton, border: "1px solid "+t.border, width: "36px", height: "36px", fontWeight: 700, fontSize: "14px", "&:disabled": { opacity: 0.4, cursor: "not-allowed" } },
        pageActive: { backgroundColor: t.accent + " !important", color: t.accentText + " !important", borderColor: t.accent },
        rowsSelect: { ...s.input, width: "auto", minHeight: "36px", padding: "4px 8px", cursor: "pointer", appearance: "auto" },
        empty: { padding: "40px 16px", textAlign: "center", color: t.textMuted },
        menuPaper: { backgroundColor: t.dark ? "#1A2238" : "#FFFFFF", color: t.text, border: "1px solid "+t.border, "& .MuiMenuItem-root": { fontSize: "14px", gap: "10px", "& svg": { width: "18px", height: "18px" } } },
        menuDanger: { color: t.danger.fg + " !important" },
        srOnly: { position: "absolute", width: "1px", height: "1px", padding: 0, margin: "-1px", overflow: "hidden", clip: "rect(0,0,0,0)", border: 0 },
        // Settings tab
        settingsGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px", [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" } },
        settingsCard: { ...s.card, padding: "18px 20px" },
        settingsCardHead: { display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "14px", "& > svg": { width: "26px", height: "26px", color: t.dark ? t.link : t.accent, flexShrink: 0, marginTop: "2px" } },
        settingsTitle: { margin: 0, fontSize: "16px", fontWeight: 700, color: t.textStrong },
        settingsText: { margin: "2px 0 0", fontSize: "13px", color: t.textMuted, lineHeight: "18px" },
        option: {
            display: "flex", gap: "12px", alignItems: "flex-start", padding: "8px 6px", borderRadius: "8px", cursor: "pointer",
            "&:hover": { backgroundColor: t.surfaceHover },
            "& input": { width: "18px", height: "18px", marginTop: "2px", accentColor: t.accent, cursor: "pointer", flexShrink: 0 },
            "& input:disabled": { cursor: "not-allowed" }
        },
        optionTitle: { display: "block", fontSize: "14px", fontWeight: 600, color: t.text, "& em": { fontStyle: "normal", fontWeight: 400, color: t.textMuted } },
        optionText: { display: "block", fontSize: "13px", color: t.textMuted, lineHeight: "18px", marginTop: "1px" },
        fieldset: { border: "none", margin: 0, padding: 0, minWidth: 0 },
        legend: { padding: 0, marginBottom: "4px", fontSize: "12px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: t.textMuted },
        messageCard: { ...s.card, padding: "18px 20px", gridColumn: "1 / -1" },
        textarea: { ...s.input, minHeight: "88px", resize: "vertical", lineHeight: "21px" },
        rowEnd: { display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap", marginTop: "14px" },
        secondaryButton: s.secondaryButton,
        cancelButton: { ...s.secondaryButton, borderColor: t.borderStrong, color: t.text, "&:hover": { backgroundColor: t.surfaceHover } },
        accentButton: s.accentButton,
        placeholders: { margin: "6px 0 0", fontSize: "12px", color: t.textMuted, "& code": { padding: "1px 5px", borderRadius: "4px", backgroundColor: t.surfaceAlt, border: "1px solid "+t.border, color: t.text, fontSize: "12px" } },
        preview: { marginTop: "10px", padding: "10px 12px", borderRadius: "8px", border: "1px dashed "+t.border, fontSize: "13px", whiteSpace: "pre-wrap", color: t.textMuted },
        // Audit
        resultCell: { whiteSpace: "nowrap" },
        reason: { fontSize: "13px", color: t.textMuted, maxWidth: "240px" }
    };
};

export default farPageStyle;
